import React from 'react';
import { LegalPage } from '../components/LegalPage';

const Policies = () => (
  <LegalPage
    testId="policies-page"
    eyebrow="How we work"
    title="Company Policies"
    updated="July 2026"
  >
    <p>
      This page summarises the operational policies we follow across privacy,
      refunds, cancellations, communication and data handling. Read alongside our
      <a href="/terms"> Terms &amp; Conditions</a>.
    </p>

    <h2>Privacy &amp; data protection</h2>
    <p>
      We collect the minimum information required to fulfil a booking:
      your name, mobile number, email, pickup &amp; delivery addresses, and payment
      reference. Sensitive identifiers (Aadhaar, PAN) are collected only when
      legally required and are stored encrypted; the last 4 digits are masked in
      all internal dashboards.
    </p>
    <ul>
      <li>Your data is <strong>never sold</strong> to advertisers or third parties.</li>
      <li>Payment is processed via <strong>Razorpay</strong>; we never see or store your card / UPI credentials.</li>
      <li>Booking records are retained for 7 years as required by Indian tax law, then permanently deleted.</li>
      <li>You may request a full data export or account deletion by writing to <a href="mailto:support@luggagefreejourney.com">support@luggagefreejourney.com</a>.</li>
    </ul>

    <h2>Refund policy</h2>
    <ul>
      <li><strong>Before pickup</strong> — 100% refund minus the payment gateway fee (typically 2–3%).</li>
      <li><strong>After pickup, before delivery</strong> — 50% refund; bag returned to the original address at no extra cost.</li>
      <li><strong>Edit-driven refund</strong> — if you edit a paid booking to a lower total, the difference is queued as a pending refund and released by our admin team within 24 working hours.</li>
      <li><strong>Delay-driven refund</strong> — automatic partial refund if we delay delivery beyond 24 hours due to our own operational fault.</li>
      <li>All refunds are credited to the original payment source (UPI / card / net-banking) within 5–10 business days.</li>
    </ul>

    <h2>Cancellation policy</h2>
    <p>
      You may cancel a booking any time from the <strong>My Bookings</strong>
      dashboard while the status is BOOKED, NEW BOOKING, PAID or SCHEDULED.
      Once the status turns PICKED_UP, cancellation is treated as a "return to
      origin" and is subject to the after-pickup refund rule above.
    </p>

    <h2>Damage &amp; loss claims</h2>
    <ul>
      <li>Inspect your bag at delivery and note any visible damage on the delivery-OTP receipt.</li>
      <li>Raise a claim within <strong>24 hours</strong> of delivery via <a href="mailto:support@luggagefreejourney.com">support@luggagefreejourney.com</a>, with:
        <ul>
          <li>the baggage tag reference (e.g. LFJ411430),</li>
          <li>photos of the damaged bag and, where applicable, the item(s) inside,</li>
          <li>proof of value (receipt, invoice, or bank statement) for items above ₹5,000.</li>
        </ul>
      </li>
      <li>We investigate every claim within 3 working days and settle valid claims within 7 business days.</li>
    </ul>

    <h2>Communication &amp; consent</h2>
    <p>
      At registration you consent to receive booking-related updates via
      <strong> SMS and WhatsApp</strong> on your registered mobile. These messages
      include OTPs, status transitions (PICKED_UP → IN_TRANSIT → DELIVERED),
      payment confirmations, and the final PDF invoice.
    </p>
    <ul>
      <li>We do <strong>not</strong> send promotional or marketing messages without an opt-in.</li>
      <li>To opt out of non-critical WhatsApp messages, reply "STOP" to any of our messages.</li>
      <li>Transactional OTPs and delivery confirmations cannot be opted out of — they are required for the Service to function safely.</li>
    </ul>

    <h2>Prohibited items policy</h2>
    <p>
      Certain items must never be handed to us — see Section 3 of the
      <a href="/terms"> Terms &amp; Conditions</a>. Any bag found to contain a
      prohibited item may be handed over to the appropriate authorities and
      insurance cover is void.
    </p>

    <h2>Agent &amp; partner conduct</h2>
    <ul>
      <li>All field agents wear a uniform and carry a photo ID; feel free to verify before handover.</li>
      <li>Every pickup and drop-off is captured on the operator's device with a timestamp.</li>
      <li>Agents may never open your bag. If a scan is legally required (e.g. inter-state security), you will be notified before it happens.</li>
    </ul>

    <h2>Grievance redressal</h2>
    <p>
      If your issue is not resolved to your satisfaction via
      <a href="mailto:support@luggagefreejourney.com"> support@luggagefreejourney.com</a>,
      you may escalate to our Grievance Officer at the same address with the
      subject line "GRIEVANCE — &lt;your baggage tag&gt;". We respond to
      escalations within <strong>2 business days</strong>.
    </p>

    <p style={{ marginTop: '2rem', color: '#737373', fontSize: '0.85rem' }}>
      Policies evolve as our operations mature — check this page periodically for
      the current version.
    </p>
  </LegalPage>
);

export default Policies;
